const MAXMART_TEXT_SEARCH_ENDPOINT =
  'https://maxmart-visual-search-503268313804.europe-west8.run.app/v1/text-search';
const MAXMART_SEARCH_MIN_CHARS = 2;
const MAXMART_RECENT_SEARCHES_KEY = 'maxmart:recent-searches:v1';
const maxmartProductDetailCache = new Map();

function maxmartSearchTokens(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .split(/[^a-z0-9+./-]+/)
    .filter((token) => token.length >= 2)
    .slice(0, 8);
}

function maxmartEscapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function maxmartAppendHighlightedText(container, value, query) {
  const text = String(value || '');
  const tokens = maxmartSearchTokens(query)
    .sort((left, right) => right.length - left.length);
  if (!tokens.length) {
    container.textContent = text;
    return;
  }
  const pattern = new RegExp(
    `(${tokens.map(maxmartEscapeRegExp).join('|')})`,
    'gi'
  );
  text.split(pattern).filter(Boolean).forEach((part) => {
    if (tokens.includes(
      part.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('it')
    )) {
      const mark = document.createElement('mark');
      mark.textContent = part;
      container.appendChild(mark);
    } else {
      container.appendChild(document.createTextNode(part));
    }
  });
}

function maxmartProductHandle(product) {
  if (product && product.handle) return String(product.handle);
  const match = String(product && product.url || '')
    .match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function maxmartNormalizeSuggestion(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .replace(/[“”"'()[\]{}]/g, ' ')
    .replace(/[^a-z0-9+./-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const MAXMART_SUGGESTION_CONNECTORS = new Set([
  'a', 'ad', 'al', 'alla', 'con', 'da', 'di', 'e', 'in', 'per', 'su'
]);

function maxmartBuildQuerySuggestions(products, searchTerm, limit = 7) {
  const query = maxmartNormalizeSuggestion(searchTerm);
  if (!query) return [];

  const queryTokens = query.split(' ');
  const lastToken = queryTokens[queryTokens.length - 1];
  const prefixTokens = queryTokens.slice(0, -1);
  const candidates = new Map();

  function addCandidate(value, score) {
    const normalized = maxmartNormalizeSuggestion(value);
    if (
      normalized.length < 2 ||
      normalized.length > 72 ||
      normalized.split(' ').length > 5
    ) return;
    const suggestionWords = normalized.split(' ');
    if (
      MAXMART_SUGGESTION_CONNECTORS.has(
        suggestionWords[suggestionWords.length - 1]
      )
    ) return;
    const current = candidates.get(normalized);
    if (!current || score > current.score) {
      candidates.set(normalized, { value: normalized, score });
    }
  }

  products.forEach((product, productIndex) => {
    const title = maxmartNormalizeSuggestion(product.title);
    const vendor = maxmartNormalizeSuggestion(product.vendor);
    const productType = maxmartNormalizeSuggestion(product.productType);
    const titleWords = title.split(' ').filter(Boolean);
    const combinedWords = [
      ...titleWords,
      ...vendor.split(' '),
      ...productType.split(' ')
    ].filter(Boolean);
    const previousTokensMatch = prefixTokens.every((token) =>
      combinedWords.some((word) => word.startsWith(token))
    );
    const baseScore = 120 - productIndex * 4;

    [title, productType].filter(Boolean).forEach((source) => {
      const words = source.split(' ').filter(Boolean);

      for (let length = 1; length <= Math.min(5, words.length); length += 1) {
        const phrase = words.slice(0, length).join(' ');
        if (phrase.startsWith(query)) {
          addCandidate(phrase, baseScore - length);
        }
      }

      words.forEach((word, wordIndex) => {
        if (!word.startsWith(lastToken) || !previousTokensMatch) return;
        const completed = [...prefixTokens, word].join(' ');
        addCandidate(completed, baseScore + 5);

        const continuation = [word];
        for (
          let index = wordIndex + 1;
          index < words.length && continuation.length < 4;
          index += 1
        ) {
          const nextWord = words[index];
          if (/\d/.test(nextWord) || nextWord.length > 18) break;
          continuation.push(nextWord);
          addCandidate(
            [...prefixTokens, ...continuation].join(' '),
            baseScore - continuation.length
          );
        }
      });
    });

    if (vendor) {
      if (vendor.startsWith(query)) addCandidate(vendor, baseScore + 8);
      if (previousTokensMatch && vendor.startsWith(lastToken)) {
        addCandidate([...prefixTokens, vendor].join(' '), baseScore + 10);
      }
      if (queryTokens.length === 1) {
        const matchingProductWord = titleWords.find((word) =>
          word.startsWith(lastToken)
        );
        if (matchingProductWord) {
          addCandidate(
            `${matchingProductWord} ${vendor}`,
            baseScore - 2
          );
        }
      }
    }
  });

  addCandidate(query, 1);
  return Array.from(candidates.values())
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      const wordDifference =
        left.value.split(' ').length - right.value.split(' ').length;
      return wordDifference || left.value.localeCompare(right.value, 'it');
    })
    .slice(0, limit)
    .map((candidate) => candidate.value);
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    window.__maxmartPredictiveSearchCount =
      (window.__maxmartPredictiveSearchCount || 0) + 1;
    this.instanceId = `maxmart-search-${window.__maxmartPredictiveSearchCount}`;
    this.cachedResults = {};
    this.abortController = null;
    this.requestSequence = 0;
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form.search');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.clearButton = null;
    this.isOpen = false;

    if (!this.input || !this.form || !this.predictiveSearchResults) return;
    this.prepareMarkup();
    this.setupEventListeners();
  }

  prepareMarkup() {
    const originalInputId = this.input.id;
    const inputId = `${this.instanceId}-input`;
    const resultsId = `${this.instanceId}-results-list`;

    this.input.id = inputId;
    this.input.setAttribute('aria-owns', resultsId);
    this.input.setAttribute('aria-controls', resultsId);
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('spellcheck', 'false');
    this.input.placeholder = 'Cerca tra 380.000 prodotti';

    const label = originalInputId
      ? this.querySelector(`label[for="${originalInputId}"]`)
      : null;
    if (label) label.setAttribute('for', inputId);

    if (!this.form.querySelector('input[name="type"]')) {
      const productType = document.createElement('input');
      productType.type = 'hidden';
      productType.name = 'type';
      productType.value = 'product';
      this.form.appendChild(productType);
    }

    const prefixInput = this.form.querySelector('input[name="options[prefix]"]');
    if (prefixInput) prefixInput.value = 'last';

    const submitButton = this.form.querySelector('.search-header__submit');
    if (submitButton) {
      submitButton.type = 'submit';
      submitButton.setAttribute('aria-label', 'Avvia la ricerca');
    }

    this.clearButton = document.createElement('button');
    this.clearButton.type = 'button';
    this.clearButton.className = 'mm-search-clear';
    this.clearButton.setAttribute('aria-label', 'Cancella ricerca');
    this.clearButton.hidden = true;
    this.clearButton.innerHTML =
      '<svg aria-hidden="true" viewBox="0 0 24 24">' +
      '<path d="M6 6l12 12M18 6 6 18"/></svg>';
    if (submitButton && submitButton.parentNode) {
      submitButton.parentNode.insertBefore(this.clearButton, submitButton);
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'mm-search-mobile-close';
    closeButton.setAttribute('aria-label', 'Chiudi ricerca');
    closeButton.innerHTML =
      '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M15.5 5 8.5 12l7 7"/></svg>';
    closeButton.addEventListener('click', () => {
      this.input.blur();
      this.close();
    });
    this.prepend(closeButton);
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener(
      'input',
      debounce(() => this.onChange(), 180).bind(this)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.updateClearButton();
        this.renderDiscoveryState();
        this.open();
        this.input.focus();
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    this.updateClearButton();
    if (!searchTerm.length) {
      this.renderDiscoveryState();
      this.open();
      return;
    }
    if (searchTerm.length < MAXMART_SEARCH_MIN_CHARS) {
      this.renderHintState();
      this.open();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery();
    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (!searchTerm.length) {
      event.preventDefault();
      return;
    }
    this.saveRecentSearch(searchTerm);
    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();
    this.updateClearButton();
    if (!searchTerm.length) {
      this.renderDiscoveryState();
      this.open();
      return;
    }
    if (searchTerm.length < MAXMART_SEARCH_MIN_CHARS) {
      this.renderHintState();
      this.open();
      return;
    }
    if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 160);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    if (event.code === 'Escape') {
      this.input.blur();
      this.close();
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
      this.switchOption(event.code === 'ArrowUp' ? 'up' : 'down');
      return;
    }
    if (event.code === 'Enter') {
      const selected = this.querySelector(
        '[aria-selected="true"] a, [aria-selected="true"] button'
      );
      if (selected) {
        event.preventDefault();
        selected.click();
        return;
      }
      if (this.getQuery().length) {
        event.preventDefault();
        this.form.requestSubmit();
      }
    }
  }

  updateClearButton() {
    if (this.clearButton) this.clearButton.hidden = !this.getQuery().length;
  }

  readRecentSearches() {
    try {
      const parsed = JSON.parse(
        window.localStorage.getItem(MAXMART_RECENT_SEARCHES_KEY) || '[]'
      );
      return Array.isArray(parsed)
        ? parsed.filter((value) => typeof value === 'string').slice(0, 5)
        : [];
    } catch {
      return [];
    }
  }

  saveRecentSearch(value) {
    const query = String(value || '').trim();
    if (query.length < MAXMART_SEARCH_MIN_CHARS) return;
    try {
      const next = [
        query,
        ...this.readRecentSearches().filter(
          (item) =>
            item.toLocaleLowerCase('it') !== query.toLocaleLowerCase('it')
        )
      ].slice(0, 5);
      window.localStorage.setItem(
        MAXMART_RECENT_SEARCHES_KEY,
        JSON.stringify(next)
      );
    } catch {
      // La ricerca resta pienamente utilizzabile anche senza memoria locale.
    }
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;
    const options = Array.from(
      this.querySelectorAll('.predictive-search__list-item')
    );
    if (!options.length) return;

    const selectedIndex = options.findIndex(
      (option) => option.getAttribute('aria-selected') === 'true'
    );
    let nextIndex;
    if (direction === 'up') {
      nextIndex = selectedIndex <= 0 ? options.length - 1 : selectedIndex - 1;
    } else {
      nextIndex = selectedIndex >= options.length - 1 ? 0 : selectedIndex + 1;
    }

    options.forEach((option, index) => {
      option.setAttribute('aria-selected', index === nextIndex ? 'true' : 'false');
    });
    const activeElement = options[nextIndex];
    this.input.setAttribute('aria-activedescendant', activeElement.id);
    this.setLiveRegionText(activeElement.textContent.trim());
    activeElement.scrollIntoView({ block: 'nearest' });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLocaleLowerCase('it');
    this.setLiveRegionLoadingState();
    this.renderLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSmartResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestSequence;
    const useNativeProducts = maxmartSearchTokens(searchTerm).length > 1;
    const nativeProductsPromise = useNativeProducts
      ? this.fetchShopifyProducts(searchTerm, this.abortController.signal)
          .catch(() => [])
      : Promise.resolve([]);

    fetch(MAXMART_TEXT_SEARCH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ q: searchTerm, limit: 8 }),
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Smart search ${response.status}`);
        return response.json();
      })
      .then(async (data) => {
        if (
          requestId !== this.requestSequence ||
          this.getQuery() !== searchTerm
        ) return;
        const backendProducts = Array.isArray(data.products) ? data.products : [];
        const serverSuggestions = Array.isArray(data.suggestions)
          ? data.suggestions.slice(0, 7)
          : [];
        const nativeProducts = await nativeProductsPromise;
        if (
          requestId !== this.requestSequence ||
          this.getQuery() !== searchTerm
        ) return;
        const products = nativeProducts.length
          ? nativeProducts
          : backendProducts;
        if (!products.length) {
          const result = {
            products: [],
            suggestions: serverSuggestions
          };
          this.cachedResults[queryKey] = result;
          this.renderSmartResults(result, searchTerm);
          return;
        }
        const enriched = await maxmartEnrichProducts(products, 8);
        if (
          requestId !== this.requestSequence ||
          this.getQuery() !== searchTerm
        ) return;
        const result = {
          products: enriched,
          suggestions: serverSuggestions
        };
        this.cachedResults[queryKey] = result;
        this.renderSmartResults(result, searchTerm);
      })
      .catch(async (error) => {
        if (error.name === 'AbortError') return;
        if (useNativeProducts) {
          const nativeProducts = await nativeProductsPromise;
          if (
            requestId !== this.requestSequence ||
            this.getQuery() !== searchTerm
          ) return;
          if (nativeProducts.length) {
            const enriched = await maxmartEnrichProducts(nativeProducts, 8);
            const result = {
              products: enriched,
              suggestions: maxmartBuildQuerySuggestions(
                enriched,
                searchTerm,
                7
              )
            };
            this.cachedResults[queryKey] = result;
            this.renderSmartResults(result, searchTerm);
            return;
          }
        }
        this.getShopifyFallback(searchTerm, queryKey);
      });
  }

  fetchShopifyProducts(searchTerm, signal) {
    const fields = [
      'title',
      'product_type',
      'variants.title',
      'variants.sku',
      'variants.barcode',
      'vendor'
    ].join(',');
    const url =
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
      `&${encodeURIComponent('resources[type]')}=product` +
      `&${encodeURIComponent('resources[limit]')}=8` +
      `&${encodeURIComponent('resources[options][fields]')}=${encodeURIComponent(fields)}` +
      '&section_id=predictive-search';

    return fetch(url, signal ? { signal } : undefined)
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (this.getQuery() !== searchTerm) return [];
        const section = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search markup missing');
        const seen = new Set();
        return Array.from(
          section.querySelectorAll('a[href*="/products/"]')
        ).reduce((output, link) => {
          const url = link.getAttribute('href') || '';
          if (!url || seen.has(url)) return output;
          seen.add(url);
          const image = link.querySelector('img');
          const titleElement = link.querySelector(
            '.predictive-search__item-heading'
          );
          const vendorElement = link.querySelector(
            '.predictive-search__item-vendor'
          );
          output.push({
            title: String(
              titleElement && titleElement.textContent ||
              image && image.alt ||
              ''
            ).trim(),
            vendor: String(
              vendorElement && vendorElement.textContent || ''
            ).trim(),
            url,
            image: image && (image.currentSrc || image.src) || ''
          });
          return output;
        }, []).slice(0, 8);
      });
  }

  getShopifyFallback(searchTerm, queryKey) {
    this.fetchShopifyProducts(searchTerm)
      .then(async (products) => {
        if (this.getQuery() !== searchTerm) return;
        const enriched = await maxmartEnrichProducts(products, 8);
        if (this.getQuery() !== searchTerm) return;
        const result = {
          products: enriched,
          suggestions: maxmartBuildQuerySuggestions(
            enriched,
            searchTerm,
            7
          )
        };
        this.cachedResults[queryKey] = result;
        this.renderSmartResults(result, searchTerm);
      })
      .catch(() => this.renderSmartResults([], searchTerm));
  }

  renderLoadingState() {
    const root = document.createElement('div');
    root.className = 'mm-predictive-search-results mm-search-is-loading';

    const header = document.createElement('div');
    header.className = 'mm-predictive-search__header';
    const heading = document.createElement('h2');
    heading.className = 'predictive-search__heading';
    heading.textContent = 'Cerco i prodotti più pertinenti';
    header.appendChild(heading);

    const skeletons = document.createElement('div');
    skeletons.className = 'mm-search-skeletons';
    for (let index = 0; index < 6; index += 1) {
      const skeleton = document.createElement('span');
      skeleton.className = 'mm-search-skeleton';
      skeleton.setAttribute('aria-hidden', 'true');
      skeletons.appendChild(skeleton);
    }
    root.append(header, skeletons);
    this.predictiveSearchResults.replaceChildren(root);
    this.open();
  }

  renderHintState() {
    const root = document.createElement('div');
    root.className = 'mm-search-message';
    const title = document.createElement('strong');
    title.textContent = 'Continua a scrivere';
    const message = document.createElement('span');
    message.textContent =
      'Bastano 2 caratteri. Puoi usare nome, marca, codice, misura o potenza.';
    root.append(title, message);
    this.predictiveSearchResults.replaceChildren(root);
    this.removeAttribute('results');
  }

  renderDiscoveryState() {
    const root = document.createElement('div');
    root.className = 'mm-search-discovery';

    const intro = document.createElement('div');
    intro.className = 'mm-search-discovery__intro';
    const title = document.createElement('strong');
    title.textContent = 'Trova subito il prodotto giusto';
    const message = document.createElement('span');
    message.textContent =
      'Cerca per nome, marca, modello, codice articolo, dimensione o potenza.';
    intro.append(title, message);
    root.appendChild(intro);

    const recent = this.readRecentSearches();
    if (recent.length) {
      const recentTitle = document.createElement('span');
      recentTitle.className = 'mm-search-discovery__label';
      recentTitle.textContent = 'Ricerche recenti';
      root.appendChild(recentTitle);

      const list = document.createElement('ul');
      list.className =
        'mm-search-recent-list predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'listbox');
      recent.forEach((value, index) => {
        const item = document.createElement('li');
        item.id = `${this.instanceId}-recent-${index + 1}`;
        item.className = 'predictive-search__list-item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mm-search-recent';
        button.tabIndex = -1;
        button.textContent = value;
        button.addEventListener('click', () => {
          this.input.value = value;
          this.updateClearButton();
          this.getSearchResults(value);
          this.input.focus();
        });
        item.appendChild(button);
        list.appendChild(item);
      });
      root.appendChild(list);
    }

    const criteria = document.createElement('div');
    criteria.className = 'mm-search-criteria';
    ['Prodotto', 'Brand e modello', 'Codice', 'Misura o potenza']
      .forEach((value) => {
        const criterion = document.createElement('span');
        criterion.textContent = value;
        criteria.appendChild(criterion);
      });
    root.appendChild(criteria);

    this.predictiveSearchResults.replaceChildren(root);
    this.removeAttribute('results');
  }

  namespaceResultIds(markup) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = markup;
    const idMap = new Map();
    wrapper.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      const newId = `${this.instanceId}-${oldId}`;
      idMap.set(oldId, newId);
      element.id = newId;
    });
    wrapper
      .querySelectorAll('[aria-labelledby],[aria-controls],[aria-owns]')
      .forEach((element) => {
        ['aria-labelledby', 'aria-controls', 'aria-owns'].forEach((attribute) => {
          const value = element.getAttribute(attribute);
          if (value && idMap.has(value)) {
            element.setAttribute(attribute, idMap.get(value));
          }
        });
      });
    return wrapper.innerHTML;
  }

  renderSmartResults(result, searchTerm) {
    const products = Array.isArray(result)
      ? result
      : Array.isArray(result && result.products)
        ? result.products
        : [];
    const root = document.createElement('div');
    root.className = 'mm-predictive-search-results mm-query-first';

    const providedSuggestions = Array.isArray(result && result.suggestions)
      ? result.suggestions
          .map(maxmartNormalizeSuggestion)
          .filter(Boolean)
          .slice(0, 7)
      : [];
    const suggestions = providedSuggestions.length
      ? providedSuggestions
      : maxmartBuildQuerySuggestions(products, searchTerm, 7);
    const suggestionsSection = document.createElement('section');
    suggestionsSection.className = 'mm-query-suggestions';
    const suggestionsHeading = document.createElement('h2');
    suggestionsHeading.id = `${this.instanceId}-suggestions-heading`;
    suggestionsHeading.className = 'mm-search-section-heading';
    suggestionsHeading.textContent = 'Suggerimenti';

    const suggestionsList = document.createElement('ul');
    suggestionsList.id = `${this.instanceId}-results-list`;
    suggestionsList.className =
      'mm-query-suggestions__list predictive-search__results-list list-unstyled';
    suggestionsList.setAttribute('role', 'listbox');
    suggestionsList.setAttribute(
      'aria-labelledby',
      suggestionsHeading.id
    );

    suggestions.forEach((suggestion, index) => {
      const item = document.createElement('li');
      item.id = `${this.instanceId}-suggestion-${index + 1}`;
      item.className = 'predictive-search__list-item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mm-query-suggestion';
      button.tabIndex = -1;
      const icon = document.createElement('span');
      icon.className = 'mm-query-suggestion__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="6.5"/>' +
        '<path d="m16 16 4 4"/></svg>';
      const label = document.createElement('span');
      label.className = 'mm-query-suggestion__label';
      maxmartAppendHighlightedText(label, suggestion, searchTerm);
      const arrow = document.createElement('span');
      arrow.className = 'mm-query-suggestion__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↗';
      button.append(icon, label, arrow);
      button.addEventListener('click', () => {
        this.input.value = suggestion;
        this.updateClearButton();
        this.saveRecentSearch(suggestion);
        this.form.requestSubmit();
      });
      item.appendChild(button);
      suggestionsList.appendChild(item);
    });
    suggestionsSection.append(suggestionsHeading, suggestionsList);
    root.appendChild(suggestionsSection);

    if (products.length) {
      const productsSection = document.createElement('section');
      productsSection.className = 'mm-product-suggestions';
      const productsHeader = document.createElement('div');
      productsHeader.className = 'mm-product-suggestions__header';
      const productsHeading = document.createElement('h2');
      productsHeading.className = 'mm-search-section-heading';
      productsHeading.textContent = 'Prodotti suggeriti';
      const productsHint = document.createElement('span');
      productsHint.textContent = 'Scorri';
      productsHeader.append(productsHeading, productsHint);

      const productsList = document.createElement('ul');
      productsList.className = 'mm-product-suggestions__track list-unstyled';
      productsList.setAttribute('role', 'list');
      products.slice(0, 6).forEach((product) => {
        const item = document.createElement('li');
        item.className = 'mm-product-suggestion';

        const link = document.createElement('a');
        link.href = product.url;
        link.className = 'mm-product-suggestion__link link link--text';
        link.addEventListener('click', () =>
          this.saveRecentSearch(searchTerm)
        );

        const media = document.createElement('span');
        media.className = 'mm-product-suggestion__media';
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = product.title || '';
          image.width = 144;
          image.height = 144;
          image.loading = 'lazy';
          media.appendChild(image);
        }

        const content = document.createElement('span');
        content.className = 'mm-product-suggestion__content';
        if (product.vendor) {
          const vendor = document.createElement('span');
          vendor.className = 'mm-product-suggestion__vendor';
          vendor.textContent = product.vendor;
          content.appendChild(vendor);
        }
        const title = document.createElement('span');
        title.className = 'mm-product-suggestion__title';
        title.textContent = product.title || '';
        content.appendChild(title);
        if (Number.isFinite(product.priceCents)) {
          const price = document.createElement('strong');
          price.className = 'mm-product-suggestion__price';
          price.textContent = maxmartFormatPrice(product.priceCents);
          content.appendChild(price);
        }

        link.append(media, content);
        item.appendChild(link);
        productsList.appendChild(item);
      });
      productsSection.append(productsHeader, productsList);
      root.appendChild(productsSection);
    }

    const searchAllButton = document.createElement('button');
    searchAllButton.type = 'button';
    searchAllButton.className = 'mm-search-submit-all';
    searchAllButton.textContent = products.length
      ? `Vedi tutti i risultati per “${searchTerm}”`
      : `Cerca “${searchTerm}” in tutto il catalogo`;
    searchAllButton.addEventListener('click', () => {
      this.saveRecentSearch(searchTerm);
      this.form.requestSubmit();
    });
    root.appendChild(searchAllButton);
    this.predictiveSearchResults.replaceChildren(root);
    this.setAttribute('results', 'true');
    this.setLiveRegionResults(
      suggestions.length + products.length,
      'suggerimenti disponibili'
    );
    this.open();
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.setLiveRegionResults(
      this.querySelectorAll('.predictive-search__list-item').length
    );
    this.open();
  }

  setLiveRegionLoadingState() {
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text') || 'Caricamento';
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    clearTimeout(this.liveRegionTimer);
    this.liveRegionTimer = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults(count, label = 'prodotti trovati') {
    this.removeAttribute('loading');
    this.setLiveRegionText(`${count} ${label}`);
  }

  getResultsMaxHeight() {
    const header = document.getElementById('shopify-section-header');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    return Math.max(280, window.innerHeight - headerBottom);
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      window.matchMedia('(max-width: 749px)').matches
        ? ''
        : `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('mm-predictive-search-open');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (this.abortController) this.abortController.abort();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.predictiveSearchResults.removeAttribute('style');
    document.documentElement.classList.remove('mm-predictive-search-open');
    this.isOpen = false;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

function maxmartFormatPrice(cents) {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value / 100);
}

async function maxmartLoadProductDetails(product) {
  const handle = maxmartProductHandle(product);
  if (!handle) return product;
  if (!maxmartProductDetailCache.has(handle)) {
    const request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product detail ${response.status}`);
        return response.json();
      })
      .then((detail) => {
        const variants = Array.isArray(detail.variants) ? detail.variants : [];
        const selectedVariant =
          variants.find((variant) => variant.available !== false) ||
          variants[0] ||
          {};
        const compareAtValues = variants
          .map((variant) => Number(variant.compare_at_price))
          .filter(Number.isFinite);
        return {
          handle,
          title: String(detail.title || ''),
          url: `/products/${handle}`,
          image: String(
            detail.featured_image ||
            (Array.isArray(detail.images) && detail.images[0]) ||
            ''
          ),
          vendor: String(detail.vendor || ''),
          productType: String(detail.type || ''),
          priceCents: Number(detail.price),
          compareAtPriceCents: compareAtValues.length
            ? Math.max(...compareAtValues)
            : Number(detail.compare_at_price),
          available: detail.available !== false,
          sku: String(selectedVariant.sku || ''),
          variantId: selectedVariant.id || null
        };
      })
      .catch((error) => {
        maxmartProductDetailCache.delete(handle);
        throw error;
      });
    maxmartProductDetailCache.set(handle, request);
  }
  try {
    const detail = await maxmartProductDetailCache.get(handle);
    return {
      ...product,
      ...detail,
      title: detail.title || product.title,
      url: detail.url || product.url,
      image: detail.image || product.image,
      vendor: product.vendor || detail.vendor
    };
  } catch {
    return product;
  }
}

async function maxmartEnrichProducts(products, limit = products.length) {
  const selected = products.slice(0, limit);
  const output = new Array(selected.length);
  let cursor = 0;
  async function worker() {
    while (cursor < selected.length) {
      const index = cursor;
      cursor += 1;
      output[index] = await maxmartLoadProductDetails(selected[index]);
    }
  }
  await Promise.all(
    Array.from(
      { length: Math.min(8, selected.length) },
      () => worker()
    )
  );
  return output.filter(Boolean);
}

function maxmartBuildSearchCard(product, query, index) {
  const item = document.createElement('li');
  item.className = 'grid__item mm-smart-search__grid-item';

  const card = document.createElement('article');
  card.className = 'mm-smart-search-card';

  const link = document.createElement('a');
  link.className = 'mm-smart-search-card__link';
  link.href = product.url;

  const media = document.createElement('span');
  media.className = 'mm-smart-search-card__media';
  if (index === 0) {
    const best = document.createElement('span');
    best.className = 'mm-smart-search-card__best';
    best.textContent =
      product.match === 'exact' ? 'Corrispondenza esatta' : 'Più pertinente';
    media.appendChild(best);
  }
  if (
    Number.isFinite(product.compareAtPriceCents) &&
    Number.isFinite(product.priceCents) &&
    product.compareAtPriceCents > product.priceCents
  ) {
    const discount = document.createElement('span');
    discount.className = 'mm-smart-search-card__discount';
    discount.textContent =
      `−${Math.round(
        (1 - product.priceCents / product.compareAtPriceCents) * 100
      )}%`;
    media.appendChild(discount);
  }
  if (product.image) {
    const image = document.createElement('img');
    image.src = product.image;
    image.alt = product.title || '';
    image.loading = 'lazy';
    image.width = 360;
    image.height = 360;
    media.appendChild(image);
  }

  const content = document.createElement('span');
  content.className = 'mm-smart-search-card__content';
  if (product.vendor) {
    const vendor = document.createElement('span');
    vendor.className = 'mm-smart-search-card__vendor';
    vendor.textContent = product.vendor;
    content.appendChild(vendor);
  }
  const title = document.createElement('span');
  title.className = 'mm-smart-search-card__title';
  maxmartAppendHighlightedText(title, product.title, query);
  content.appendChild(title);

  const meta = document.createElement('span');
  meta.className = 'mm-smart-search-card__meta';
  if (product.sku) {
    const sku = document.createElement('span');
    sku.textContent = `Cod. ${product.sku}`;
    meta.appendChild(sku);
  }
  if (typeof product.available === 'boolean') {
    const availability = document.createElement('span');
    availability.className = product.available
      ? 'is-available'
      : 'is-unavailable';
    availability.textContent = product.available
      ? 'Disponibile'
      : 'Non disponibile';
    meta.appendChild(availability);
  }
  if (meta.childNodes.length) content.appendChild(meta);

  if (Number.isFinite(product.priceCents)) {
    const priceGroup = document.createElement('span');
    priceGroup.className = 'mm-smart-search-card__prices';
    const price = document.createElement('strong');
    price.className = 'mm-smart-search-card__price';
    price.textContent = maxmartFormatPrice(product.priceCents);
    priceGroup.appendChild(price);
    if (
      Number.isFinite(product.compareAtPriceCents) &&
      product.compareAtPriceCents > product.priceCents
    ) {
      const compare = document.createElement('s');
      compare.textContent = maxmartFormatPrice(product.compareAtPriceCents);
      priceGroup.appendChild(compare);
    }
    content.appendChild(priceGroup);
  }

  const action = document.createElement('span');
  action.className = 'mm-smart-search-card__action';
  action.textContent = 'Dettagli prodotto';
  const arrow = document.createElement('span');
  arrow.setAttribute('aria-hidden', 'true');
  arrow.textContent = '→';
  action.appendChild(arrow);
  content.appendChild(action);

  link.append(media, content);
  card.appendChild(link);
  item.appendChild(card);
  return item;
}

function maxmartCreateSearchSelect(labelText, options) {
  const label = document.createElement('label');
  label.className = 'mm-smart-search-control';
  const text = document.createElement('span');
  text.textContent = labelText;
  const select = document.createElement('select');
  options.forEach(({ value, label: optionLabel }) => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = optionLabel;
    select.appendChild(option);
  });
  label.append(text, select);
  return { label, select };
}

function maxmartCreateSearchToggle(labelText) {
  const label = document.createElement('label');
  label.className = 'mm-smart-search-toggle';
  const input = document.createElement('input');
  input.type = 'checkbox';
  const text = document.createElement('span');
  text.textContent = labelText;
  label.append(input, text);
  return { label, input };
}

function maxmartBuildSearchToolbar(products) {
  const toolbar = document.createElement('section');
  toolbar.className = 'mm-smart-search-toolbar';
  toolbar.setAttribute('aria-label', 'Filtri dei risultati più pertinenti');

  const summary = document.createElement('div');
  summary.className = 'mm-smart-search-toolbar__summary';
  const summaryText = document.createElement('div');
  const title = document.createElement('strong');
  title.textContent = 'Risultati più pertinenti';
  const count = document.createElement('span');
  count.className = 'mm-smart-search-toolbar__count';
  count.setAttribute('role', 'status');
  summaryText.append(title, count);
  const restore = document.createElement('button');
  restore.type = 'button';
  restore.className = 'mm-smart-search-toolbar__restore';
  restore.textContent = 'Tutto il catalogo e filtri avanzati';
  summary.append(summaryText, restore);

  const controls = document.createElement('div');
  controls.className = 'mm-smart-search-toolbar__controls';

  const vendors = Array.from(new Set(
    products.map((product) => product.vendor).filter(Boolean)
  )).sort((left, right) => left.localeCompare(right, 'it'));
  const vendor = maxmartCreateSearchSelect('Brand', [
    { value: '', label: 'Tutti i brand' },
    ...vendors.map((value) => ({ value, label: value }))
  ]);

  const maximumPrice = products.reduce(
    (maximum, product) =>
      Math.max(maximum, Number(product.priceCents) || 0),
    0
  );
  const priceLimits = [2500, 5000, 10000, 25000, 50000]
    .filter((value) => value < maximumPrice);
  const price = maxmartCreateSearchSelect('Prezzo', [
    { value: '', label: 'Qualsiasi prezzo' },
    ...priceLimits.map((value) => ({
      value: String(value),
      label: `Fino a ${maxmartFormatPrice(value)}`
    }))
  ]);
  const sort = maxmartCreateSearchSelect('Ordina', [
    { value: 'relevance', label: 'Più pertinenti' },
    { value: 'price-asc', label: 'Prezzo crescente' },
    { value: 'price-desc', label: 'Prezzo decrescente' },
    { value: 'title', label: 'Nome prodotto' }
  ]);
  const available = maxmartCreateSearchToggle('Solo disponibili');
  const sale = maxmartCreateSearchToggle('In offerta');
  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'mm-smart-search-toolbar__reset';
  reset.textContent = 'Azzera filtri';

  controls.append(
    vendor.label,
    price.label,
    sort.label,
    available.label,
    sale.label,
    reset
  );
  toolbar.append(summary, controls);
  return {
    toolbar,
    count,
    restore,
    reset,
    vendor: vendor.select,
    price: price.select,
    sort: sort.select,
    available: available.input,
    sale: sale.input
  };
}

async function maxmartEnhanceSearchPage() {
  if (window.location.pathname !== '/search') return;
  const params = new URLSearchParams(window.location.search);
  const query = String(params.get('q') || '').trim();
  const searchType = params.get('type');
  if (query.length < 2 || (searchType && searchType !== 'product')) return;

  const productGrid = document.querySelector('#product-grid ul.product-grid');
  if (!productGrid || productGrid.dataset.maxmartSmartSearch === 'true') return;

  productGrid.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(MAXMART_TEXT_SEARCH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ q: query, limit: 24 })
    });
    if (!response.ok) throw new Error(`Smart search ${response.status}`);
    const data = await response.json();
    const products = Array.isArray(data.products) ? data.products : [];
    if (!products.length) return;

    const enriched = (await maxmartEnrichProducts(products, 24))
      .map((product, index) => ({ ...product, relevanceIndex: index }));
    const originalItems = Array.from(productGrid.children);
    const heading = document.querySelector('.template-search__header h1');
    const originalHeading = heading ? heading.textContent : '';
    const facets = document.querySelector('#main-search-filters');
    const sortControls = document.querySelector(
      'facet-filters-form.facets-vertical-sort'
    );
    const pagination = document.querySelector('#product-grid .pagination-wrapper');
    const originalVisibility = {
      facets: facets ? facets.hidden : false,
      sort: sortControls ? sortControls.hidden : false,
      pagination: pagination ? pagination.hidden : false
    };
    const toolbar = maxmartBuildSearchToolbar(enriched);

    function renderProducts() {
      let visible = enriched.filter((product) => {
        if (toolbar.vendor.value && product.vendor !== toolbar.vendor.value) {
          return false;
        }
        if (
          toolbar.price.value &&
          (!Number.isFinite(product.priceCents) ||
            product.priceCents > Number(toolbar.price.value))
        ) {
          return false;
        }
        if (toolbar.available.checked && product.available === false) {
          return false;
        }
        if (
          toolbar.sale.checked &&
          !(
            Number.isFinite(product.compareAtPriceCents) &&
            Number.isFinite(product.priceCents) &&
            product.compareAtPriceCents > product.priceCents
          )
        ) {
          return false;
        }
        return true;
      });
      if (toolbar.sort.value === 'price-asc') {
        visible = visible.sort(
          (left, right) =>
            (Number(left.priceCents) || Infinity) -
            (Number(right.priceCents) || Infinity)
        );
      } else if (toolbar.sort.value === 'price-desc') {
        visible = visible.sort(
          (left, right) =>
            (Number(right.priceCents) || -1) -
            (Number(left.priceCents) || -1)
        );
      } else if (toolbar.sort.value === 'title') {
        visible = visible.sort((left, right) =>
          left.title.localeCompare(right.title, 'it')
        );
      } else {
        visible = visible.sort(
          (left, right) => left.relevanceIndex - right.relevanceIndex
        );
      }

      const fragment = document.createDocumentFragment();
      visible.forEach((product, index) => {
        fragment.appendChild(maxmartBuildSearchCard(product, query, index));
      });
      if (!visible.length) {
        const empty = document.createElement('li');
        empty.className = 'mm-smart-search-empty';
        const title = document.createElement('strong');
        title.textContent = 'Nessun prodotto con questi filtri';
        const message = document.createElement('span');
        message.textContent =
          'Azzera uno o più filtri oppure consulta tutto il catalogo.';
        empty.append(title, message);
        fragment.appendChild(empty);
      }
      productGrid.replaceChildren(fragment);
      toolbar.count.textContent =
        `${visible.length} di ${enriched.length} prodotti`;
    }

    [
      toolbar.vendor,
      toolbar.price,
      toolbar.sort,
      toolbar.available,
      toolbar.sale
    ].forEach((control) => control.addEventListener('change', renderProducts));
    toolbar.reset.addEventListener('click', () => {
      toolbar.vendor.value = '';
      toolbar.price.value = '';
      toolbar.sort.value = 'relevance';
      toolbar.available.checked = false;
      toolbar.sale.checked = false;
      renderProducts();
    });
    toolbar.restore.addEventListener('click', () => {
      productGrid.replaceChildren(...originalItems);
      delete productGrid.dataset.maxmartSmartSearch;
      document.body.classList.remove('mm-smart-search-page');
      toolbar.toolbar.remove();
      if (heading) heading.textContent = originalHeading;
      if (facets) facets.hidden = originalVisibility.facets;
      if (sortControls) sortControls.hidden = originalVisibility.sort;
      if (pagination) pagination.hidden = originalVisibility.pagination;
    });

    productGrid.before(toolbar.toolbar);
    renderProducts();
    productGrid.dataset.maxmartSmartSearch = 'true';
    document.body.classList.add('mm-smart-search-page');
    if (heading) {
      heading.textContent =
        `Risultati per “${query}”`;
    }
    if (facets) facets.hidden = true;
    if (sortControls) sortControls.hidden = true;
    if (pagination) pagination.hidden = true;
  } catch {
    productGrid.removeAttribute('aria-busy');
    return;
  }
  productGrid.removeAttribute('aria-busy');
}

// La pagina completa conserva intenzionalmente griglia, filtri e schede native
// MaxMart. Il motore intelligente interviene prima, nell'autocomplete, senza
// sostituire la coerenza visiva e funzionale già presente nel catalogo.
